<script setup lang="ts">
import type { IBankByRelatedRates } from './types'
import { TableRow } from '~/components/ui/table'

const props = defineProps<{
  banks: IBankByRelatedRates[]
  currency: string
  action: 'buy' | 'sell'
  rate: string | number
}>()

const banksWithParsedRates = computed(() => {
  return props.banks?.map((bank) => {
    const rateKey =
      `${props.action}_${props.currency.toLowerCase()}` as keyof IBankByRelatedRates['rates']
    const raw = bank.rates[rateKey]
    const parsedRate = raw ? parseFloat(String(raw)) : undefined

    return {
      ...bank,
      rate: parsedRate,
      time: useDateTimeFormat(bank.created_at)
    }
  })
})
</script>

<template>
  <Table
    class="whitespace-nowrap text-xs sm:text-base [&_td]:p-1 sm:[&_td]:p-4 [&_th]:p-1 sm:[&_th]:p-4"
  >
    <TableHeader>
      <TableRow class="lowercase">
        <TableHead>{{ $t('common.title_column') }}</TableHead>
        <TableHead class="w-[100px] text-center">
          {{ props.action === 'buy' ? $t('common.buy') : $t('common.sell') }}
        </TableHead>
        <TableHead class="w-[100px] text-center">{{ $t('common.time') }}</TableHead>
      </TableRow>
    </TableHeader>

    <TableBody>
      <TableRow v-for="bank in banksWithParsedRates" :key="bank.url">
        <TableCell class="flex items-center gap-1 sm:max-w-none">
          <NuxtLinkLocale
            :to="bank.url"
            :aria-label="`${$t('common.more_about')} ${bank.title}`"
            class="flex w-full items-center gap-1 overflow-hidden font-semibold sm:gap-2"
          >
            <NuxtImg
              :src="bank.logo"
              :alt="`${$t('common.logo_alt')} ${bank.title}`"
              class="h-7 w-7 flex-shrink-0"
            />
            <span class="w-full truncate text-xs hover:underline sm:text-base">
              {{ bank.title }}
            </span>
          </NuxtLinkLocale>
        </TableCell>

        <TableCell class="w-[100px] bg-gray-100 text-center">
          {{ bank.rate !== undefined ? bank.rate : '—' }}
        </TableCell>

        <TableCell class="w-[100px] text-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger as-child>
                <span class="block cursor-help text-center text-green-500">
                  {{ bank.time }}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>
                  {{ $t('widget.rates.related_banks.rate_actual') }}<br />{{
                    $t('widget.rates.related_banks.set_at')
                  }}
                  {{ bank.time }}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TableCell>
      </TableRow>
    </TableBody>
  </Table>
</template>
